import { axios } from '../../clients/axios'
import FileDownload from 'js-file-download'
import nodeFormData from 'form-data'
import { getTenantName } from '../../util/user'
import { HeaderNames } from '../common'
import { flattenPropertyUnitList } from '../../util/unit-list'

export default {
  namespaced: true,
  state: {
    beds: [],
    occupancy: {
      vacant: 0,
      occupied: 0,
      total: 0
    },
    due: {
      upcoming: 0,
      overdue: 0,
      due: 0
    },
    selectedBed: {},
    dueTable: {
      upcoming: [],
      overdue: [],
      due: []
    },
    upcomingMoveOut: [],
    monthEvents: {},
    units: [],
    batchTenantsImportPreview: [],
    showUnitId: false,
    batchUnitsImportPreview: [],
    draggableEvents: [],
    propertyUnitList: [],
    propertyUnitListFlat: [],
    /**
     * @type {Record<number, {
     *  bed: object,
     *  hasUnpaidPaymentRequest: boolean,
     *  hasPendingApprovalPaymentRequest: boolean,
     *  hasPendingApprovalPaidPayment: boolean,
     *  lease: object,
     *  tenant: object,
     *  alias: object,
     *  endOfLease: string,
     * }>}
     */
    propertyUnitListSupplementaryDataMap: {},
    /**
     * @type {Array<{
     *  bed: object,
     *  hasUnpaidPaymentRequest: boolean,
     *  hasPendingApprovalPaymentRequest: boolean,
     *  hasPendingApprovalPaidPayment: boolean,
     *  lease: object,
     *  tenant: object,
     *  alias: object,
     *  endOfLease: string,
     * }>}
     */
    propertyUnitListSupplementaryData: [],
    propertyUnitListSupplementaryDataLoading: false
  },
  getters: {
    getOccupancy: (state) => {
      return state.occupancy
    },
    getSelectedBed: (state, getters, rootState, rootGetters) => {
      const aliases = rootGetters['users/getAliases']
      const alias = aliases.find(
        (a) => a.tenantId === (state.selectedBed && state.selectedBed.tenantId)
      )
      state.selectedBed.tenantAlias = alias && alias.name
      state.selectedBed.tenantContact = alias && alias.contactNumber
      return state.selectedBed
    },
    getDueTable: (state) => {
      return state.dueTable
    },
    getUpcomingMoveOut: (state) => {
      return state.upcomingMoveOut
    },
    getMonthEvents: (state) => {
      return state.monthEvents
    },
    // TODO: Replace this with flat request
    getUnitsFlat: (state, getters, rootState, rootGetters) => {
      const aliases = rootGetters['users/getAliases']

      return state.units.map((unit) => {
        unit.tenantName = getTenantName(
          aliases,
          unit.tenantId,
          unit.tenant_details
        )
        return unit
      })
    },
    getBatchTenantsImportPreview: (state) => {
      return state.batchTenantsImportPreview
    },
    getBatchUnitsImportPreview: (state) => {
      return state.batchUnitsImportPreview
    },
    getDraggableEvents: (state) => {
      return state.draggableEvents
    },
    getPropertyUnitList: (state) => {
      return state.propertyUnitList
    },
    getPropertyUnitListFlat: (state) => {
      return state.propertyUnitListFlat
    },
    getPropertyUnitListSupplementaryData: (state) => {
      return state.propertyUnitListSupplementaryData
    },
    getPropertyUnitListSupplementaryDataMap: (state) => {
      return state.propertyUnitListSupplementaryDataMap
    },
    isPropertyUnitListSupplementaryDataLoading: (state) => {
      return state.propertyUnitListSupplementaryDataLoading
    }
  },
  mutations: {
    fetchPropertyUnitList(state, propertyUnitList) {
      state.propertyUnitList = propertyUnitList
      state.propertyUnitListFlat = flattenPropertyUnitList([
        ...propertyUnitList.units
      ])
    },
    setPropertyUnitListSupplementaryDataLoading(state, loading) {
      state.propertyUnitListSupplementaryDataLoading = loading
    },
    fetchPropertyUnitListSupplementaryData(state, data) {
      // Clear the supplementary data map
      state.propertyUnitListSupplementaryDataMap = {}

      /**
       * Generate a hashmap of the supplementary data with the bedId as the key
       */
      state.propertyUnitListSupplementaryData = data.map((entry) => {
        entry.combinedTags = [
          entry.bed.bedName,
          (entry.tenant && entry.tenant.username) || '',
          (entry.tenant && entry.tenant.firstName) || '',
          (entry.tenant && entry.tenant.lastName) || '',
          (entry.tenant && entry.tenant.email1) || '',
          (entry.alias && entry.alias.name) || ''
        ].join(' ')
        state.propertyUnitListSupplementaryDataMap[String(entry.bed.bedId)] =
          entry

        return entry
      })

      // Reassign the object to trigger a reactivity update
      state.propertyUnitListSupplementaryDataMap = {
        ...state.propertyUnitListSupplementaryDataMap
      }
    },
    fetchOccupancy(state, occupancy) {
      state.occupancy = occupancy
    },
    fetchDue(state, due) {
      state.due = due
    },
    fetchSelectedBed(state, bed) {
      state.selectedBed = bed
    },
    fetchDueTable(state, result) {
      state.dueTable.due = result.due
      state.dueTable.upcoming = result.upcoming
      state.dueTable.overdue = result.overdue
    },
    fetchUpcomingMoveOut(state, beds) {
      state.upcomingMoveOut = beds
    },
    fetchMonthEvents(state, monthEvents) {
      state.monthEvents = monthEvents
    },
    fetchUnitsFlat(state, units) {
      state.units = units
    },
    fetchBatchTenantsImportPreview(state, entries) {
      state.batchTenantsImportPreview = entries
    },
    toggleShowUnitId(state) {
      state.showUnitId = !state.showUnitId
    },
    fetchBatchUnitsImportPreview(state, entries) {
      state.batchUnitsImportPreview = entries
    },
    pushDraggableEvents(state, entry) {
      state.draggableEvents.push(entry)
    },
    clearDraggableEvents(state) {
      state.draggableEvents = []
    }
  },
  actions: {
    async fetchPropertyUnitList(context) {
      const propertyUnitList = await axios.get(
        `${process.env.VUE_APP_API_URL}/unitList`,
        { withCredentials: true }
      )
      const data = propertyUnitList.data
      context.commit('fetchPropertyUnitList', data || [])
    },
    async fetchPropertyUnitListSupplementaryData(context) {
      context.commit('setPropertyUnitListSupplementaryDataLoading', true)
      const supplementaryData = await axios.get(
        `${process.env.VUE_APP_API_URL}/unitList/supplementaryData`,
        { withCredentials: true }
      )
      const data = supplementaryData.data
      context.commit('fetchPropertyUnitListSupplementaryData', data || [])
      context.commit('setPropertyUnitListSupplementaryDataLoading', false)
    },
    async fetchOccupancyAction(context) {
      const occupancy = await axios.get(
        `${process.env.VUE_APP_API_URL}/beds/count/occupancy`,
        { withCredentials: true }
      )
      context.commit('fetchOccupancy', occupancy.data)

      return occupancy.data
    },
    async fetchDueAction(context) {
      const due = await axios.get(
        `${process.env.VUE_APP_API_URL}/beds/count/due`,
        { withCredentials: true }
      )
      context.commit('fetchDue', due.data)

      return due.data
    },
    async fetchSelectedBedAction(context, bedId) {
      const promises = await Promise.all([
        axios.get(`${process.env.VUE_APP_API_URL}/beds/details/${bedId}`, {
          withCredentials: true
        }),
        context.dispatch('users/fetchAliasesAction', null, { root: true })
      ])
      const bed = promises[0]

      context.commit('fetchSelectedBed', bed.data)
      return bed.data
    },
    async fetchDueTableAction(context) {
      const due = await axios.get(`${process.env.VUE_APP_API_URL}/beds/due`, {
        withCredentials: true
      })
      const upcoming = await axios.get(
        `${process.env.VUE_APP_API_URL}/beds/upcoming`,
        { withCredentials: true }
      )
      const overdue = await axios.get(
        `${process.env.VUE_APP_API_URL}/beds/overdue`,
        { withCredentials: true }
      )

      const result = {
        due: due.data,
        upcoming: upcoming.data,
        overdue: overdue.data
      }
      context.commit('fetchDueTable', result)

      return result
    },
    async getTenantOccupied(context, params) {
      const { tenantId } = params
      try {
        await axios.get(
          `${process.env.VUE_APP_API_URL}/beds/tenant/${tenantId}/occupy`,
          { withCredentials: true }
        )
        return Promise.resolve(true)
      } catch (err) {
        let response = 'There was an error processing your request.'
        if (err.response.status === 403) {
          //logout, maybe
          response = 'The user is already occupying another unit.'
        }
        return Promise.reject(response)
      }
    },
    async putTenantToBed(context, params) {
      const {
        tenantId,
        bedId,
        addressId,
        downPayment,
        collectionDay,
        daysBeforeNotif,
        paidUntilDate,
        nextRentalPeriod,
        contractPeriods,
        alias,
        securityDeposit,
        idempotencyKey
      } = params
      try {
        const form = new nodeFormData()
        if (params['attachments']) {
          await params['attachments'].map((file) => {
            form.append('attachments', file)
          })
        }
        form.append('tenantId', tenantId)
        form.append('bedId', bedId)
        form.append('addressId', addressId)
        form.append('colletionDay', collectionDay)
        form.append('daysBeforeNotif', JSON.stringify(daysBeforeNotif))
        form.append('paidUntilDate', paidUntilDate)
        form.append('nextRentalPeriod', JSON.stringify(nextRentalPeriod))
        form.append('contractPeriods', JSON.stringify(contractPeriods))
        form.append('alias', JSON.stringify(alias))
        if (downPayment) {
          form.append('downPayment', JSON.stringify(downPayment))
        }
        if (securityDeposit) {
          form.append('securityDeposit', JSON.stringify(securityDeposit))
        }
        const headers = {
          [HeaderNames.CONTENT_TYPE]: 'multipart/form-data',
          [HeaderNames.IDEMPOTENCY_KEY]: idempotencyKey
        }

        const unit = await axios.put(
          `${process.env.VUE_APP_API_URL}/beds/tenant`,
          form,
          {
            headers: headers,
            withCredentials: true
          }
        )
        return Promise.resolve(unit.data)
      } catch (err) {
        if (err.response.status === 401) {
          //logout, maybe
        }
        return Promise.reject(err.response)
      }
    },
    /**
     *
     * @param {*} context
     * @param {{
     *  bedName: string,
     *  tags: {text: string, value: string}[],
     *  parentAddressId: string,
     *  index: number
     * }} params
     * @returns
     */
    async newBedAction(context, params) {
      const { bedName, groupId, tags, parentAddressId, index } = params
      try {
        const bed = await axios.post(
          `${process.env.VUE_APP_API_URL}/beds`,
          { bedName, groupId, tags, parentAddressId, index },
          { withCredentials: true }
        )

        return Promise.resolve(bed.data)
      } catch (err) {
        let response = ''
        if (err.response.data === 'UNIT_LIMIT_ERROR') {
          response =
            'Your property has exceeded the unit limit. Please reduce the selected units, or increase your subscription limit.'
        }
        return Promise.reject(response)
      }
    },
    async deleteBedAction(context, params) {
      const { bedId, addressId } = params
      try {
        const bed = await axios.delete(
          `${process.env.VUE_APP_API_URL}/beds/${bedId}`,
          { data: { addressId }, withCredentials: true },
          { withCredentials: true }
        )

        return Promise.resolve(bed)
      } catch (err) {
        return Promise.reject(err.response)
      }
    },
    async updateBedNameAction(context, params) {
      const { bedName, bedId, addressId } = params
      try {
        const bed = await axios.put(
          `${process.env.VUE_APP_API_URL}/beds/bedName/${bedId}`,
          { bedName, addressId },
          { withCredentials: true }
        )

        return Promise.resolve(bed.data)
      } catch (err) {
        let response = ''
        if (err.response.status === 500) {
          response =
            'There was an error updating the unit name. Please try again.'
        }
        return Promise.reject(response)
      }
    },
    async updateBedContactAction(context, params) {
      const { contact, tenantId } = params
      try {
        const bed = await axios.put(
          `${process.env.VUE_APP_API_URL}/beds/contact/${tenantId}`,
          { contact },
          { withCredentials: true }
        )

        return Promise.resolve(bed.data)
      } catch (err) {
        let response = ''
        if (err.response.status === 500) {
          response =
            'There was an error updating the contact. Please try again.'
        }
        return Promise.reject(response)
      }
    },
    async newGroupAction(context, params) {
      const { groupName, parentAddressId } = params
      try {
        const group = await axios.post(
          `${process.env.VUE_APP_API_URL}/unitList/insert`,
          { name: groupName, parentAddressId, index: 0, type: 'group' },
          { withCredentials: true }
        )

        return Promise.resolve(group.data)
      } catch (err) {
        let response = 'There was an error processing your request.'
        // if(err.response.data === 'UNIT_LIMIT_ERROR') {
        //   response = 'Your property has exceeded the unit limit. Please reduce the selected units, or increase your subscription limit.'
        // }
        // else {
        //   response = ''
        // }
        return Promise.reject(response)
      }
    },
    async updateGroupNameAction(context, params) {
      const { groupName, addressId } = params
      try {
        const bed = await axios.post(
          `${process.env.VUE_APP_API_URL}/unitList/rename`,
          { name: groupName, addressId },
          { withCredentials: true }
        )

        return Promise.resolve(bed.data)
      } catch (err) {
        let response = ''
        if (err.response.data === 'UNIT_LIMIT_ERROR') {
          response =
            'Your property has exceeded the unit limit. Please reduce the selected units, or increase your subscription limit.'
        }
        return Promise.reject(response)
      }
    },
    async deleteGroupAction(context, params) {
      const { addressId } = params
      try {
        const unitListResponse = await axios.post(
          `${process.env.VUE_APP_API_URL}/unitList/remove`,
          { addressId, type: 'group' },
          { withCredentials: true }
        )

        return unitListResponse
      } catch (err) {
        let response = ''
        switch (err.response.data) {
          case 'REMOVE_CHILD_PROPERTYUNITLIST_HAS_CHILDREN':
            response = 'Cannot delete group while a tenant is occupying a unit'
            break
          case 'REMOVE_CHILD_PROPERTYUNITLIST_NOT_FOUND':
            response = 'Group not found'
            break
          case 'REMOVE_CHILD_PROPERTYUNITLIST_NOT_A_GROUP':
            response =
              'Error trying to delete a group, but the addressId is not a group'
            break
        }

        return Promise.reject(response)
      }
    },
    async createMultipleAction(context, params) {
      const {
        floors,
        units,
        beds,
        startingFloor,
        selectedForms,
        unitScheme,
        bedScheme,
        utilitiesScheme
      } = params
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/groups/multiple`,
          {
            numFloors: floors,
            numUnits: units,
            numBeds: beds,
            startingFloor: startingFloor,
            forms: selectedForms,
            unitScheme: unitScheme,
            bedScheme: bedScheme,
            utilitiesScheme: utilitiesScheme
          },
          { withCredentials: true }
        )

        return Promise.resolve(response)
      } catch (err) {
        let response = ''
        if (err.response.data === 'UNIT_LIMIT_ERROR') {
          response =
            'Selected floors / units / beds has exceeded the unit limit. Please reduce the selected units, or increase your subscription limit.'
        }
        return Promise.reject(new Error(response))
      }
    },
    async createApartmentMultipleAction(context, params) {
      const {
        floors,
        units,
        startingFloor,
        selectedForms,
        unitScheme,
        utilitiesScheme
      } = params
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/groups/multipleApartment`,
          {
            numFloors: floors,
            numUnits: units,
            startingFloor: startingFloor,
            forms: selectedForms,
            unitScheme: unitScheme,
            utilitiesScheme: utilitiesScheme
          },
          { withCredentials: true }
        )

        return Promise.resolve(response)
      } catch (err) {
        let response = ''
        if (err.response.data === 'UNIT_LIMIT_ERROR') {
          response =
            'Selected floors / units / beds has exceeded the unit limit. Please reduce the selected units, or increase your subscription limit.'
        }
        return Promise.reject(new Error(response))
      }
    },
    async updateBedAction(context, params) {
      const {
        bedId,
        bedName,
        tenantId,
        rentFee,
        collectionDay,
        downPayment,
        paidUntilDate,
        moveInDate,
        moveOutDate,
        daysBeforeNotif,
        status,
        forms,
        leaseId
      } = params

      const response = await axios.put(
        `${process.env.VUE_APP_API_URL}/beds/details/${bedId}`,
        {
          bedName,
          tenantId,
          rentFee,
          collectionDay,
          downPayment,
          paidUntilDate,
          moveInDate,
          moveOutDate,
          daysBeforeNotif,
          status,
          forms,
          leaseId
        },
        { withCredentials: true }
      )

      return response
    },
    async terminateAction(context, params) {
      const { bedId, tenantId, message } = params

      const response = await axios.put(
        `${process.env.VUE_APP_API_URL}/beds/evict`,
        {
          bedId,
          tenantId,
          message
        },
        { withCredentials: true }
      )

      return response
    },
    async preterminateAction(context, params) {
      const { leaseId, reason } = params

      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/beds/preTerminate`,
        {
          leaseId,
          preTerminated: true,
          reason
        },
        { withCredentials: true }
      )

      return response
    },
    async transferTenantAction(context, params) {
      const { oldBedId, newBedId, tenantId, leaseId } = params

      const response = await axios.put(
        `${process.env.VUE_APP_API_URL}/beds/tenant/transfer`,
        {
          oldBedId,
          newBedId,
          tenantId,
          leaseId
        },
        { withCredentials: true }
      )

      return response
    },
    async fetchUpcomingMoveOutAction(context, params) {
      const { after } = params
      const beds = await axios.put(
        `${process.env.VUE_APP_API_URL}/beds/upcomingMoveOut`,
        {
          after: after
        },
        { withCredentials: true }
      )
      context.commit('fetchUpcomingMoveOut', beds.data)

      return beds.data
    },
    async fetchSOAAction(context, params) {
      const { tenantId, name } = params
      const SOA = await axios.get(
        `${process.env.VUE_APP_API_URL}/beds/details/${tenantId}/SOA`,
        { withCredentials: true, responseType: 'blob' }
      )

      FileDownload(SOA.data, `SOA - ${name}.pdf`)

      return SOA.data
    },
    async fetchMultipleSOAAction(context, params) {
      const { tenants, name } = params
      const multipleSOA = await axios.post(
        `${process.env.VUE_APP_API_URL}/beds/multiplesoa`,
        tenants,
        { withCredentials: true, responseType: 'blob' }
      )

      FileDownload(multipleSOA.data, `SOA - ${name}.zip`)

      return multipleSOA.data
    },
    async fetchMonthEventsAction(context, params) {
      const { month, year } = params

      const monthEvents = await axios.get(
        `${process.env.VUE_APP_API_URL}/beds/monthEvents/${year}/${month}`,
        {
          withCredentials: true
        }
      )
      context.commit('fetchMonthEvents', monthEvents.data)

      return monthEvents.data
    },
    async fetchTenantBalanceAction(context, params) {
      const { tenantId } = params

      const tenantBalance = await axios.get(
        `${process.env.VUE_APP_API_URL}/balance/property/${tenantId}`,
        {
          withCredentials: true
        }
      )

      return parseFloat(tenantBalance.data.balance)
    },
    async fetchUnitsFlatAction(context, params) {
      const { propertyId } = params

      const units = await axios.post(
        `${process.env.VUE_APP_API_URL}/beds/getUnits`,
        {
          propertyId: propertyId
        },
        {
          withCredentials: true
        }
      )

      context.commit('fetchUnitsFlat', units.data)

      return Promise.resolve(units.data)
    },
    async saveNotesAction(context, params) {
      const { leaseId, bedId, notes } = params

      const result = axios.post(
        `${process.env.VUE_APP_API_URL}/beds/notes`,
        {
          leaseId: leaseId,
          bedId: bedId,
          notes: notes
        },
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result.data)
    },
    async saveAreaAction(context, params) {
      const { bedId, area } = params

      const result = axios.post(
        `${process.env.VUE_APP_API_URL}/beds/area`,
        {
          bedId: bedId,
          area: area
        },
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result.data)
    },
    async getUnits(context, params) {
      const { status } = params

      const units = await axios.get(
        `${process.env.VUE_APP_API_URL}/beds/search?status=${status}`,
        {
          withCredentials: true
        }
      )

      return Promise.resolve(units.data)
    },
    async linkUnitsAction(context, params) {
      const { vacantBedId, chosenBedId } = params

      const result = await axios.post(
        `${process.env.VUE_APP_API_URL}/beds/link`,
        {
          vacantBedId: vacantBedId,
          chosenBedId: chosenBedId
        },
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result)
    },
    async unlinkUnitAction(context, params) {
      const { bedId } = params

      const result = await axios.post(
        `${process.env.VUE_APP_API_URL}/beds/unlink`,
        {
          bedId: bedId
        },
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result)
    },
    async saveRentFeeAction(context, params) {
      const { leaseId, rentFee } = params

      const result = axios.post(
        `${process.env.VUE_APP_API_URL}/beds/rentFee`,
        {
          leaseId: leaseId,
          rentFee: rentFee
        },
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result.data)
    },
    async moveUnitAction(context, params) {
      const { index, futureIndex, element } = params

      const result = axios.post(
        `${process.env.VUE_APP_API_URL}/groups/move`,
        {
          index: index,
          futureIndex: futureIndex,
          element: element
        },
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result.data)
    },
    async renewLeaseAction(context, params) {
      const { leaseId, contractPeriods, daysBeforeNotif } = params

      const result = axios.put(
        `${process.env.VUE_APP_API_URL}/beds/contractPeriod`,
        {
          leaseId: leaseId,
          contractPeriods: contractPeriods,
          daysBeforeNotif: daysBeforeNotif
        },
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result.data)
    },
    async updateDueDateAction(context, params) {
      const { leaseId, dueDate } = params

      const result = axios.post(
        `${process.env.VUE_APP_API_URL}/beds/dueDate`,
        {
          leaseId: leaseId,
          dueDate: dueDate
        },
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result.data)
    },
    async updateSendNotificationAction(context, params) {
      const { leaseId, sendNotification } = params

      const result = axios.post(
        `${process.env.VUE_APP_API_URL}/beds/setSendNotification`,
        {
          leaseId: leaseId,
          sendNotification: sendNotification
        },
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result.data)
    },
    async saveRentalPeriodAction(context, params) {
      const { leaseId, startDate, endDate } = params

      const result = await axios.post(
        `${process.env.VUE_APP_API_URL}/beds/rentalPeriod`,
        {
          leaseId,
          startDate,
          endDate
        },
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result.data)
    },

    async uploadAttachmentsAction(context, params) {
      try {
        const form = new nodeFormData()
        form.append('leaseId', params.leaseId)
        await params['attachments'].map((file) => {
          form.append('attachments', file)
        })

        const headers = {
          'Content-Type': 'multipart/form-data'
        }

        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/beds/attachments`,
          form,
          {
            headers: headers,
            withCredentials: true
          }
        )

        return Promise.resolve(res)
      } catch (err) {
        return Promise.reject(err.response)
      }
    },
    async deleteAttachmentAction(context, params) {
      try {
        const res = await axios.delete(
          `${process.env.VUE_APP_API_URL}/beds/attachments/delete`,
          { data: params, withCredentials: true }, //leaseId and attachment
          { withCredentials: true }
        )

        return Promise.resolve(res)
      } catch (err) {
        if (err.response.status === 401) {
          //logout, maybe
        }
        return Promise.reject(err.response)
      }
    },
    async getGroupTagAction(context, groupId) {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API_URL}/groups/tags/${groupId}`,
          { withCredentials: true }
        )

        return Promise.resolve(res)
      } catch (err) {
        if (err.response.status === 401) {
          //logout, maybe
        }
        return Promise.reject(err.response)
      }
    },
    async addGroupTagAction(context, params) {
      let { groupId, tag } = params
      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/groups/tags/${groupId}`,
          { tag: tag },
          { withCredentials: true }
        )

        return Promise.resolve(res)
      } catch (err) {
        if (err.response.status === 401) {
          //logout, maybe
        }
        return Promise.reject(err.response)
      }
    },
    async deleteGroupTagAction(context, params) {
      let { groupId, tag } = params
      try {
        const res = await axios.delete(
          `${process.env.VUE_APP_API_URL}/groups/tags/${groupId}`,
          { data: { tag }, withCredentials: true },
          { withCredentials: true }
        )

        return Promise.resolve(res)
      } catch (err) {
        if (err.response.status === 401) {
          //logout, maybe
        }
        return Promise.reject(err.response)
      }
    },
    async getUnitTagAction(context, bedId) {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API_URL}/beds/tags/${bedId}`,
          { withCredentials: true }
        )

        return Promise.resolve(res)
      } catch (err) {
        if (err.response.status === 401) {
          //logout, maybe
        }
        return Promise.reject(err.response)
      }
    },
    async addUnitTagAction(context, params) {
      let { bedId, tag } = params
      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/beds/tags/${bedId}`,
          { tag: tag },
          { withCredentials: true }
        )

        return Promise.resolve(res)
      } catch (err) {
        if (err.response.status === 401) {
          //logout, maybe
        }
        return Promise.reject(err.response)
      }
    },
    async deleteUnitTagAction(context, params) {
      let { bedId, tag } = params
      try {
        const res = await axios.delete(
          `${process.env.VUE_APP_API_URL}/beds/tags/${bedId}`,
          { data: { tag }, withCredentials: true },
          { withCredentials: true }
        )

        return Promise.resolve(res)
      } catch (err) {
        if (err.response.status === 401) {
          //logout, maybe
        }
        return Promise.reject(err.response)
      }
    },
    async uploadTenantImportFile(context, params) {
      const form = new nodeFormData()
      form.append('file', params.file)

      const headers = {
        'Content-Type': 'multipart/form-data'
      }

      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/units/batch/tenants/import`,
          form,
          {
            headers: headers,
            withCredentials: true
          }
        )

        context.commit('fetchBatchTenantsImportPreview', res.data)
        return Promise.resolve(res.data)
      } catch (err) {
        return Promise.reject(err.response)
      }
    },
    async submitBatchTenantImportEntries(context, params) {
      const { entries } = params

      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/units/batch/tenants/data`,
          {
            entries: entries.filter((entry) => entry && entry.selected)
          },
          {
            withCredentials: true
          }
        )
        return Promise.resolve(res)
      } catch (err) {
        return Promise.reject(err.response)
      }
    },
    toggleShowUnitIdAction(context) {
      context.commit('toggleShowUnitId')
    },
    async uploadUnitsImportFile(context, params) {
      const form = new nodeFormData()
      form.append('file', params.file)

      const headers = {
        'Content-Type': 'multipart/form-data'
      }

      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/groups/batch/units/preview`,
          form,
          {
            headers: headers,
            withCredentials: true
          }
        )

        context.commit('fetchBatchUnitsImportPreview', res.data)
        return Promise.resolve(res.data)
      } catch (err) {
        return Promise.reject(err.response)
      }
    },
    async submitBatchUnitImportEntries(context, params) {
      const { data } = params

      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/groups/batch/units/data`,
          {
            data
          },
          {
            withCredentials: true
          }
        )
        return Promise.resolve(res)
      } catch (err) {
        return Promise.reject(err.response)
      }
    },
    // TODO: Delete
    pushDraggableEvents(context, event) {
      context.commit('pushDraggableEvents', event)
    },
    // TODO: Delete
    async submitDraggableEvents({ commit, state }) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/unitList/move/multiple`,
          state.draggableEvents,
          { withCredentials: true }
        )
        commit('clearDraggableEvents')

        return Promise.resolve(response)
      } catch (err) {
        const response =
          'There was a problem arranging your units. Please refresh the page and try again'
        return Promise.reject(response)
      }
    },
    async moveUnit(context, params) {
      const { addressId, newIndex, newParentAddressId } = params
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/unitList/move`,
          { addressId, newIndex, newParentAddressId },
          { withCredentials: true }
        )

        context.commit('fetchPropertyUnitList', response.data || [])
        return Promise.resolve(response)
      } catch (err) {
        const response =
          'There was a problem arranging your units. Please refresh the page and try again'
        return Promise.reject(response)
      }
    },
    async fetchLeaseRentalPaymentRequests(context, params) {
      const { leaseId } = params
      const result = await axios.get(
        `${process.env.VUE_APP_API_URL}/leases/${leaseId}/rentalPaymentRequests`,
        {
          withCredentials: true
        }
      )

      return Promise.resolve(result.data)
    },
    async insertPropertyUnitListFromTemplate(context, params) {
      const { template } = params

      const result = await axios.post(
        `${process.env.VUE_APP_API_URL}/unitList/insertFromTemplate`,
        {
          template,
          withCredentials: true
        }
      )

      return Promise.resolve(result.data)
    }
  }
}
